import React, { Component } from "react";
import { compose } from "redux";
import PropTypes from "prop-types";
import { Checkbox as SCheckbox } from "semantic-ui-react";
import { Field, connectForm, FormUtils } from "@redriver/cinnamon";
import classNames from "classnames";
import FixedInputIndicator from "./FixedInputIndicator";
import {
  withPermissions,
  withCurrentUser,
} from "features/../../../shared/components/auth";
import {
  Actions,
  Targets,
} from "features/../../../shared/constants/permissions";
import FixedFieldType, { FixableTypes } from "constants/forms/FixedFieldType";
import { UserArea } from "features/../../../shared/constants/enums";

/**
 * Custom Cinnamon component that provides an interface for the FixedFieldView object
 */
class FixedCheckboxGroup extends Component {
  static propTypes = {
    label: PropTypes.node,
    actions: PropTypes.node,
    inline: PropTypes.bool,
    width: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
    fluid: PropTypes.bool,
    renderReadOnly: PropTypes.func,
    required: PropTypes.bool,
    requiredError: PropTypes.string,
    confirmField: PropTypes.string,
    confirmFieldError: PropTypes.string,
    field: PropTypes.string.isRequired,
    showErrors: PropTypes.bool,
    allErrors: PropTypes.bool,
    animateErrors: PropTypes.oneOfType([PropTypes.number, PropTypes.bool]),
    disabled: PropTypes.bool,
    readOnly: PropTypes.bool,
    customErrors: PropTypes.arrayOf(PropTypes.string),
    customValidator: PropTypes.func,
    notifiedFields: PropTypes.arrayOf(PropTypes.string),
    value: PropTypes.shape({
      id: PropTypes.string,
      value: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
      isFixed: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
    }),
    onChange: PropTypes.func.isRequired,
    errors: PropTypes.arrayOf(PropTypes.string),
    passThruProps: PropTypes.object,
    formState: PropTypes.object,
    options: PropTypes.array,

    /**
     * Adds CSS class to change display priority, use 1-5 to change display priority with 5 being the highest.
     * @default 0
     */
    displayPriority: PropTypes.number,
  };

  state = {
    fixType: FixedFieldType.None,
  };

  static defaultProps = {
    value: { value: "" },
    onChange: () => {},
    label: "",
    inline: false,
    options: [],
    displayPriority: 0,
  };

  determineFixType(value) {
    return value.isFixed ? FixedFieldType.Set : FixedFieldType.None;
  }

  componentDidMount() {
    const { value } = this.props;

    const fixType = this.determineFixType(value);

    this.setState({
      fixType,
    });
  }

  componentDidUpdate(prevProps) {
    const { value } = this.props;
    if (prevProps.value !== this.props.value) {
      const fixType = this.determineFixType(value);

      this.setState({
        fixType,
      });
    }
  }

  enforceValueConstraints = (value, fixType) => {
    const {
      value: { setValue },
    } = this.props;

    return fixType === FixedFieldType.Set ? setValue : value;
  };

  /**
   * The onChange method for the `<SCheckbox />` element of the FixedCheckboxGroup
   */
  onValueChange = (value) =>
    this.props.onChange({
      ...this.props.value,
      value,
    });

  /**
   * The onChange method for the `<FixedInputEditor />` element of the FixedCheckboxGroup
   */
  onValuePropsChange = (res) => {
    const value = this.props.value;
    this.props.onChange({
      ...value,
      ...res,
      value: res.value.toString(),
      userArea: this.props.currentUser.userArea,
    });
  };

  onBlur = (e) => {
    if (this.props.onBlur) this.props.onBlur(e);
  };

  defaultRenderReadOnly = (props) => <p>{props.value.value}</p>;

  renderReadOnly = () =>
    (this.props.renderReadOnly || this.defaultRenderReadOnly)(this.props);

  rightLabelContent = (isAdmin, fixType) => {
    const {
      disabled,
      value,
      currentUser,
      options,
      radio,
      isBoolean,
      customEditorData,
      displayPriority,
    } = this.props;

    return (
      <FixedInputIndicator
        isAdmin={isAdmin}
        fixType={fixType}
        fixableType={FixableTypes.Checkbox}
        fieldId={value.id || null}
        values={[value.value || false]}
        onForcedValuesChanged={this.onValuePropsChange}
        customEditorData={{
          options,
          radio,
          isBoolean,
          ...customEditorData,
        }}
        displayPriority={displayPriority}
        disabled={
          disabled ||
          (value?.isFixed &&
            value?.userArea == UserArea.IpsAdmin &&
            value?.userArea != currentUser.userArea)
        }
      />
    );
  };

  render() {
    const {
      value,
      errors,
      showErrors,
      allErrors,
      animateErrors,
      readOnly,
      label,
      actions,
      inline,
      width,
      fluid,
      required,
      className,
      passThruProps,
      hasPermission,
      options,
      hideIndicator = false,
    } = this.props;

    const { fixType } = this.state;

    const isAdmin = hasPermission(Targets.SheetAdmin, Actions.Edit);

    const disabled = this.props.disabled || fixType == FixedFieldType.Set;

    const hiddenIndicator =
      hideIndicator || (!isAdmin && fixType === FixedFieldType.None);
    const hasRightLabel = !hiddenIndicator;

    const semanticProps = FormUtils.omitProps(
      passThruProps,
      Object.keys(FixedCheckboxGroup.propTypes),
    );

    return (
      <div className="fix-checkbox-container">
        <Field
          inline={inline}
          required={required}
          renderReadOnly={readOnly && this.renderReadOnly}
          width={width}
          fluid={fluid}
          label={label}
          actions={actions}
          errors={FormUtils.fieldErrors(errors, showErrors, allErrors)}
          animateErrors={animateErrors}
          className={classNames("", className)}
        >
          {options.map((opt, i) => (
            <SCheckbox
              key={`checkbox_${i}`}
              {...semanticProps}
              label={opt.text}
              checked={value.value == opt.value}
              onChange={() => this.onValueChange(opt.value)}
              disabled={disabled}
              onFocus={this.onFocus}
              onBlur={this.onBlur}
              className={classNames(
                "fix-input-wrapper",
                hiddenIndicator && "fix-input-hidden",
                isAdmin && "is-admin",
              )}
            />
          ))}
        </Field>
        {hasRightLabel && this.rightLabelContent(isAdmin, fixType)}
      </div>
    );
  }
}

export default connectForm({
  displayName: (props) =>
    props.label && typeof props.label === "string" ? props.label : "Input",
  validators: [],
})(compose(withPermissions, withCurrentUser)(FixedCheckboxGroup));
